import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckIcon } from '@heroicons/react/24/outline';
import BrandLogo from '../components/shared/BrandLogo';

const PricingPage = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');

  const plans = [
    {
      name: 'Free',
      description: 'Perfect for getting started',
      price: { monthly: 0, yearly: 0 },
      features: [
        '2 platform connections',
        'Basic analytics dashboard',
        '7-day data history',
        'Email support',
        'Community access',
      ],
      limitations: [
        'Limited to 10 content posts/month',
        'Basic health monitoring',
      ],
      cta: 'Get Started',
      popular: false,
    },
    {
      name: 'Starter',
      description: 'For growing creators',
      price: { monthly: 29, yearly: 290 },
      features: [
        'Up to 5 platform connections',
        'Advanced analytics & insights',
        '90-day data history',
        'Real-time notifications',
        'Revenue tracking',
        'Brand deal management',
        'Priority email support',
        'Export reports (CSV)',
      ],
      limitations: [],
      cta: 'Start Free Trial',
      popular: false,
    },
    {
      name: 'Professional',
      description: 'For serious creators',
      price: { monthly: 79, yearly: 790 },
      features: [
        'Unlimited platform connections',
        'AI-powered anomaly detection',
        'Unlimited data history',
        'Real-time WebSocket updates',
        'Advanced revenue reconciliation',
        'Custom reports & analytics',
        'API access',
        'Priority phone & email support',
        'Export reports (CSV & PDF)',
        'Team collaboration (up to 3 members)',
      ],
      limitations: [],
      cta: 'Start Free Trial',
      popular: true,
    },
    {
      name: 'Enterprise',
      description: 'For teams & agencies',
      price: { monthly: 'Custom', yearly: 'Custom' },
      features: [
        'Everything in Professional',
        'Unlimited team members',
        'White-label options',
        'Custom integrations',
        'Dedicated account manager',
        'SLA guarantee',
        'Custom training & onboarding',
        'Advanced security features',
        'Custom data retention',
      ],
      limitations: [],
      cta: 'Contact Sales',
      popular: false,
    },
  ];

  const faqs = [
    {
      question: 'Can I change plans later?',
      answer: 'Yes! You can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle.',
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards (Visa, MasterCard, American Express) and PayPal.',
    },
    {
      question: 'Is there a free trial?',
      answer: 'Yes! All paid plans come with a 14-day free trial. No credit card required.',
    },
    {
      question: 'Can I cancel anytime?',
      answer: 'Absolutely. You can cancel your subscription at any time with no cancellation fees.',
    },
    {
      question: 'Do you offer refunds?',
      answer: 'We offer a 30-day money-back guarantee if you\'re not satisfied with our service.',
    },
    {
      question: 'What happens to my data if I cancel?',
      answer: 'You can export all your data before canceling. We retain your data for 30 days after cancellation.',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-950">
      {/* Header */}
      <nav className="bg-white dark:bg-dark-900 border-b border-gray-200 dark:border-dark-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <BrandLogo size="md" />
            </Link>

            <div className="flex items-center space-x-4">
              <Link to="/" className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                Home
              </Link>
              <Link to="/login" className="btn btn-secondary">
                Login
              </Link>
              <Link to="/register" className="btn bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 text-white shadow-md">
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-orange-50 via-pink-50 to-violet-50 dark:from-dark-900 dark:to-dark-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6"
          >
            Simple, Transparent Pricing
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto mb-8"
          >
            Choose the perfect plan for your creator journey. All plans include a 14-day free trial.
          </motion.p>

          {/* Billing Toggle */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex items-center justify-center gap-4"
          >
            <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                billingCycle === 'yearly' ? 'bg-gradient-to-r from-pink-500 to-violet-600' : 'bg-gray-200 dark:bg-dark-700'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  billingCycle === 'yearly' ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
              Yearly
            </span>
            {billingCycle === 'yearly' && (
              <span className="badge badge-success">Save 17%</span>
            )}
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`card p-8 relative flex flex-col ${
                  plan.popular ? 'ring-2 ring-violet-600 shadow-xl' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="badge bg-gradient-to-r from-pink-500 to-violet-600 text-white shadow-md px-4 py-1">Most Popular</span>
                  </div>
                )}

                <div className="mb-8 flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">{plan.description}</p>
                  <div className="flex items-baseline">
                    {typeof plan.price[billingCycle] === 'number' ? (
                      <>
                        <span className="text-4xl font-bold text-gray-900 dark:text-white">
                          ${plan.price[billingCycle]}
                        </span>
                        <span className="text-gray-600 dark:text-gray-400 ml-2">
                          /{billingCycle === 'monthly' ? 'mo' : 'yr'}
                        </span>
                      </>
                    ) : (
                      <span className="text-4xl font-bold text-gray-900 dark:text-white">
                        {plan.price[billingCycle]}
                      </span>
                    )}
                  </div>
                </div>

                <Link
                  to={plan.cta === 'Contact Sales' ? '/contact' : '/register'}
                  className={`btn w-full mb-8 ${
                    plan.popular
                      ? 'bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 text-white shadow-md'
                      : 'btn-secondary'
                  }`}
                >
                  {plan.cta}
                </Link>

                <div className="space-y-3">
                  {plan.features.map((feature, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Comparison */}
      <section className="py-20 bg-gray-100 dark:bg-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-12 text-center">
            Compare Features
          </h2>

          <div className="card p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-dark-800">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white">
                      Feature
                    </th>
                    {plans.map((plan) => (
                      <th key={plan.name} className="px-6 py-4 text-center text-sm font-semibold text-gray-900 dark:text-white">
                        {plan.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-dark-700">
                  {[
                    { feature: 'Platform Connections', values: ['1', '5', 'Unlimited', 'Unlimited'] },
                    { feature: 'Data History', values: ['7 days', '90 days', 'Unlimited', 'Unlimited'] },
                    { feature: 'Real-time Updates', values: ['—', '✓', '✓', '✓'] },
                    { feature: 'AI Anomaly Detection', values: ['—', '—', '✓', '✓'] },
                    { feature: 'API Access', values: ['—', '—', '✓', '✓'] },
                    { feature: 'Team Members', values: ['1', '1', '3', 'Unlimited'] },
                    { feature: 'Support', values: ['Email', 'Priority Email', 'Phone & Email', 'Dedicated Manager'] },
                  ].map((row, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white font-medium">
                        {row.feature}
                      </td>
                      {row.values.map((value, i) => (
                        <td key={i} className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 text-center">
                          {value}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-12 text-center">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="card p-6"
              >
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  {faq.question}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-20"
        style={{ background: 'linear-gradient(105deg, #ff7a1a 0%, #f43f7b 32%, #a855f7 66%, #3b82f6 100%)' }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/85 mb-8">
            Join thousands of creators managing their success with OmniConnX
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="btn bg-white text-violet-700 hover:bg-violet-50 shadow-md">
              Start Free Trial
            </Link>
            <Link to="/contact" className="btn bg-violet-950/70 text-white ring-2 ring-white/50 hover:bg-violet-950/80">
              Contact Sales
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white dark:bg-dark-900 border-t border-gray-200 dark:border-dark-700 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-600 dark:text-gray-400">
          <p>&copy; 2026 OmniConnX. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;
